import React, { useEffect, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Plus, Trash2, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function AdvancedBooking() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ temples: [], darshan_types: [], counters: [], shifts: [] });
  const [form, setForm] = useState({
    temple_id: user.temple_id || "", devotee_name: "", reference_name: "", mobile: "",
    num_devotees: 1, darshan_type_id: "", paid_free: "", darshan_date: "", letter_date: "", letterhead_name: "", remarks: "",
  });
  const [devotees, setDevotees] = useState([]);
  const [letter, setLetter] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/meta/form-data").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDevotee = (i, k, v) => setDevotees((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const selectedDt = meta.darshan_types.find((d) => d.id === form.darshan_type_id);
  const isFreeType = selectedDt?.paid_free === "FREE";
  const totalN = Math.max(1, Number(form.num_devotees) || 1);
  const paidN = isFreeType ? 0 : Math.min(Math.max(0, Number(form.paid_devotees ?? totalN)), totalN);
  const freeN = totalN - paidN;
  const totalAmount = (selectedDt?.amount || 0) * paidN;

  const onLetter = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) { toast.error("Letter file is too large (max 2.5 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setLetter({ name: file.name, data: reader.result });
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/protocol/advanced", { ...form, num_devotees: totalN, paid_devotees: paidN, devotees, letter_file: letter });
      setResult(r.data);
      toast.success("Application submitted successfully");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-xl mx-auto" data-testid="booking-success">
        <Card className="card-gold-top shadow-lg text-center">
          <CardContent className="p-8 space-y-4">
            <CheckCircle2 className="w-14 h-14 text-green-700 mx-auto" />
            <h2 className="font-heading text-2xl font-bold text-maroon-900">Application Submitted Successfully</h2>
            <div className="bg-gold-50 border border-gold-300 rounded-md p-4">
              <p className="text-xs uppercase tracking-widest text-gold-700 font-bold">Transaction ID</p>
              <p className="font-mono text-xl font-bold text-maroon-900" data-testid="booking-success-txn">{result.transaction_id}</p>
              <p className="mt-2 text-sm font-semibold text-orange-700" data-testid="booking-success-status">Status: APPROVAL PENDING</p>
            </div>
            {result.qr_data_url && <img src={result.qr_data_url} alt="QR" className="w-36 h-36 mx-auto" data-testid="booking-success-qr" />}
            <p className="text-xs text-slate-500">A WhatsApp confirmation has been sent to {result.mobile}.</p>
            <div className="flex gap-2 justify-center">
              <Button data-testid="booking-new-btn" onClick={() => { setResult(null); setForm({ ...form, devotee_name: "", reference_name: "", mobile: "", remarks: "" }); setDevotees([]); setLetter(null); }}
                className="bg-maroon-700 hover:bg-maroon-800 text-white">New Booking</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="advanced-booking-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-1">Advanced Protocol Booking</h1>
      <p className="text-sm text-slate-500 mb-5">Register a protocol darshan request for approval by the competent authority.</p>

      <form onSubmit={submit} className="space-y-5">
        <Card className="card-gold-top shadow-sm">
          <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Devotee & Reference Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {user.role === "SUPER_ADMIN" && (
              <div>
                <Label>Temple *</Label>
                <Select value={form.temple_id} onValueChange={(v) => set("temple_id", v)}>
                  <SelectTrigger data-testid="booking-temple-select"><SelectValue placeholder="Select Temple" /></SelectTrigger>
                  <SelectContent>{meta.temples.map((t) => <SelectItem key={t.temple_id} value={t.temple_id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Devotee Name *</Label><Input data-testid="booking-devotee-name" value={form.devotee_name} onChange={(e) => set("devotee_name", e.target.value)} required /></div>
            <div><Label>Reference Name</Label><Input data-testid="booking-recommender-input" value={form.reference_name} onChange={(e) => set("reference_name", e.target.value)} placeholder="Recommended by" /></div>
            <div><Label>Mobile Number *</Label><Input data-testid="booking-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} pattern="[0-9]{10}" title="10-digit mobile number" required /></div>
            <div><Label>Number of Devotees *</Label><Input data-testid="booking-num-devotees" type="number" min={1} max={20} value={form.num_devotees} onChange={(e) => set("num_devotees", e.target.value)} required /></div>
            <div>
              <Label>Seva / Darshan Type *</Label>
              <Select value={form.darshan_type_id} onValueChange={(v) => set("darshan_type_id", v)}>
                <SelectTrigger data-testid="booking-darshan-select"><SelectValue placeholder="Select Darshan Type" /></SelectTrigger>
                <SelectContent>{meta.darshan_types.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} {d.amount > 0 ? `(₹${d.amount})` : "(Free)"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Paid Devotees</Label>
              <Input data-testid="booking-paid-devotees" type="number" min={0} max={totalN} disabled={isFreeType}
                value={paidN} onChange={(e) => set("paid_devotees", Math.max(0, Math.min(Number(e.target.value) || 0, totalN)))} />
            </div>
            <div>
              <Label>Free Devotees (auto)</Label>
              <Input data-testid="booking-free-devotees" value={freeN} disabled className="bg-gold-50" />
            </div>
            <div><Label>Darshan Date (Advanced Booking) *</Label><Input data-testid="booking-darshan-date" type="date" required
              min={new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10)} value={form.darshan_date} onChange={(e) => set("darshan_date", e.target.value)} /></div>
            <div><Label>Letter Issue Date</Label><Input data-testid="booking-letter-date" type="date" value={form.letter_date} onChange={(e) => set("letter_date", e.target.value)} /></div>
            <div><Label>Letterhead Name</Label><Input data-testid="booking-letterhead" value={form.letterhead_name} onChange={(e) => set("letterhead_name", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Remarks</Label><Textarea data-testid="booking-remarks" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} rows={2} /></div>
            <div className="sm:col-span-2 flex items-center justify-between bg-gold-50 border border-gold-300 rounded-md px-4 py-3">
              <span className="text-xs font-bold uppercase tracking-wider text-gold-700">
                Total Amount (auto) · {selectedDt ? `₹${selectedDt.amount} × ${paidN} paid · ${freeN} free · total ${totalN}` : "Select darshan type"}
              </span>
              <span className="font-heading text-2xl font-bold text-maroon-900" data-testid="booking-total-amount">₹{totalAmount.toLocaleString("en-IN")}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-gold-top shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg text-maroon-900">Devotee Sub-Records</CardTitle>
            <Button type="button" size="sm" variant="outline" data-testid="booking-devotee-add-btn"
              onClick={() => setDevotees((r) => [...r, { name: "", aadhaar: "", mobile: "", age: "", gender: "", address: "" }])}>
              <Plus className="w-4 h-4 mr-1" /> Add Devotee
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {devotees.length === 0 && <p className="text-sm text-slate-400">No additional devotee records. Add one per accompanying devotee.</p>}
            {devotees.map((d, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 p-3 border border-[#E5D9C5] rounded-md bg-gold-50/40" data-testid={`devotee-row-${i}`}>
                <Input placeholder="Name *" value={d.name} onChange={(e) => setDevotee(i, "name", e.target.value)} data-testid={`devotee-name-${i}`} required />
                <Input placeholder="Aadhaar" value={d.aadhaar} onChange={(e) => setDevotee(i, "aadhaar", e.target.value)} data-testid={`devotee-aadhaar-${i}`} />
                <Input placeholder="Mobile" value={d.mobile} onChange={(e) => setDevotee(i, "mobile", e.target.value)} />
                <Input placeholder="Age" type="number" value={d.age} onChange={(e) => setDevotee(i, "age", e.target.value)} />
                <Select value={d.gender} onValueChange={(v) => setDevotee(i, "gender", v)}>
                  <SelectTrigger data-testid={`devotee-gender-${i}`}><SelectValue placeholder="Gender" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-1">
                  <Input placeholder="Address" value={d.address} onChange={(e) => setDevotee(i, "address", e.target.value)} />
                  <Button type="button" size="icon" variant="ghost" onClick={() => setDevotees((r) => r.filter((_, x) => x !== i))} data-testid={`devotee-remove-${i}`}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="card-gold-top shadow-sm">
          <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Recommendation Letter</CardTitle></CardHeader>
          <CardContent>
            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gold-400 rounded-md p-6 cursor-pointer hover:bg-gold-50 transition-colors" data-testid="booking-letter-upload">
              <Upload className="w-5 h-5 text-gold-600" />
              <span className="text-sm text-slate-600">{letter ? letter.name : "Upload letter (PDF / JPG / PNG, max 2.5 MB)"}</span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={onLetter} />
            </label>
          </CardContent>
        </Card>

        <Button type="submit" disabled={loading} data-testid="booking-submit-btn"
          className="w-full h-12 bg-maroon-700 hover:bg-maroon-800 text-gold-100 font-heading tracking-widest text-base">
          {loading ? "SUBMITTING…" : "SUBMIT PROTOCOL REQUEST"}
        </Button>
      </form>
    </div>
  );
}
